import { answerText } from "./logic";
import type { FeedbackForm, FeedbackResponse } from "./schemas";

/**
 * One CSV cell. A value a spreadsheet would read as a formula gets a leading
 * apostrophe, because answers are typed by attendees and an organiser opens the
 * export in Excel: `=HYPERLINK(...)` in a free-text answer is the classic CSV
 * injection. Then standard RFC 4180 quoting.
 */
export function csvCell(value: string): string {
  const guarded = /^[=+\-@\t\r\n]/.test(value) ? `'${value}` : value;
  return /[",\r\n]/.test(guarded)
    ? `"${guarded.replace(/"/g, '""')}"`
    : guarded;
}

export function toCsv(rows: string[][]): string {
  // The BOM is what makes Excel read UTF-8 rather than the system code page,
  // which would mangle every umlaut in a German answer.
  return "\uFEFF" + rows.map((row) => row.map(csvCell).join(",")).join("\r\n");
}

/**
 * One row per response. A named form leads with the respondent; an anonymous
 * one has no such column at all, since its responses carry no identity to
 * put there.
 */
export function feedbackCsv(
  form: Pick<FeedbackForm, "anonymous" | "questions">,
  responses: FeedbackResponse[],
  respondentName: (response: FeedbackResponse) => string,
): string {
  const header = [
    ...(form.anonymous ? [] : ["Respondent"]),
    ...form.questions.map((question) => question.label),
  ];
  const rows = responses.map((response) => [
    ...(form.anonymous ? [] : [respondentName(response)]),
    ...form.questions.map((question) =>
      answerText(question, response.answers[question.id]),
    ),
  ]);
  return toCsv([header, ...rows]);
}

export function csvFileName(title: string): string {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
  return `${slug || "feedback"}-responses.csv`;
}

export function downloadCsv(fileName: string, csv: string): void {
  const url = URL.createObjectURL(
    new Blob([csv], { type: "text/csv;charset=utf-8" }),
  );
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoked later, not now: some browsers start the download asynchronously.
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
